import { Component, OnInit } from '@angular/core';
import { ApplicationfeesdashboardService } from './applicationfeesdashboard.service';
import { SuperloginService } from '../superlogin/superlogin.service';

@Component({
  selector: 'app-applicationfeesdashboard',
  templateUrl: './applicationfeesdashboard.component.html',
  styleUrls: ['./applicationfeesdashboard.component.scss']
})
export class ApplicationfeesdashboardComponent implements OnInit {

  // Summary
  totalSignups: number = 0;
  successfulPayments: number = 0;
  pendingPayments: number = 0;
  failedPayments: number = 0;
  signupsWithNoTransaction: number = 0;

  // Data
  payments: any[] = [];
  filteredPayments: any[] = [];

  // UI state
  activeTab: string = 'all';
  loadingTable: boolean = false;
  loadingSummary: boolean = false;
  errorMessage: string = '';
  searchQuery: string = '';

  // Pagination
  currentPage: number = 1;
  pageSize: number = 8;

  constructor(
    private feesService: ApplicationfeesdashboardService,
    private authService: SuperloginService
  ) {}

  ngOnInit(): void {
    this.loadSummary();
    this.loadTab('all');
  }

  // ── Summary ────────────────────────────────────────────────────────────────

  loadSummary(): void {
    this.loadingSummary = true;
    this.feesService.getSummary().subscribe({
      next: (data) => {
        this.totalSignups             = data.totalSignups             || 0;
        this.successfulPayments       = data.successfulPayments       || 0;
        this.pendingPayments          = data.pendingPayments          || 0;
        this.failedPayments           = data.failedPayments           || 0;
        this.signupsWithNoTransaction = data.signupsWithNoTransaction || 0;
        this.loadingSummary = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load summary.';
        this.loadingSummary = false;
      }
    });
  }

  // ── Tab / Filter ───────────────────────────────────────────────────────────

  filterTab(tab: string): void {
    this.activeTab = tab;
    this.searchQuery = '';
    this.currentPage = 1;
    this.loadTab(tab);
  }

  loadTab(tab: string): void {
    this.loadingTable = true;
    this.payments = [];
    this.filteredPayments = [];
    this.errorMessage = '';

    this.feesService.getTransactionsByTab(tab).subscribe({
      next: (data) => {
        this.payments = data || [];
        this.applySearch();
        this.loadingTable = false;
      },
      error: () => {
        this.errorMessage = 'Failed to load transactions.';
        this.loadingTable = false;
      }
    });
  }

  // ── Search ─────────────────────────────────────────────────────────────────

  onSearch(): void {
    this.currentPage = 1;
    this.applySearch();
  }

  applySearch(): void {
    const q = this.searchQuery.toLowerCase().trim();
    this.filteredPayments = this.payments.filter(p => {
      if (!q) return true;
      const name  = ((p.signupFirstname || '') + ' ' + (p.signupLastname || '')).toLowerCase();
      const email = (p.signupEmail || '').toLowerCase();
      const phone = (p.customerPhoneNumber || '');
      return name.includes(q) || email.includes(q) || phone.includes(q);
    });
    this.currentPage = 1;
  }

  // ── Pagination ─────────────────────────────────────────────────────────────

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.filteredPayments.length / this.pageSize));
  }

  get pagedPayments(): any[] {
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredPayments.slice(start, start + this.pageSize);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  get pageStart(): number {
    if (this.filteredPayments.length === 0) return 0;
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get pageEnd(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredPayments.length);
  }

  goPage(p: number): void {
    if (p >= 1 && p <= this.totalPages) this.currentPage = p;
  }

  // ── Computed Totals ────────────────────────────────────────────────────────

  get totalAmount(): number {
    return this.filteredPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);
  }

  get allTransactionsCount(): number {
    return this.payments.length;
  }

  // ── Helpers ────────────────────────────────────────────────────────────────

  getInitials(first: string, last: string): string {
    const f = (first || '?')[0] || '?';
    const l = (last  || '')[0] || '';
    return (f + l).toUpperCase();
  }

  getStatusClass(status: string): string {
    if (!status) return 'fees-status-failed';
    const s = status.toLowerCase();
    if (s === 'success' || s === 'paid') return 'fees-status-successful';
    if (s === 'pending')                  return 'fees-status-pending';
    return 'fees-status-failed';
  }

  getStatusLabel(status: string): string {
    if (!status) return '-';
    const s = status.toLowerCase();
    if (s === 'success' || s === 'paid') return 'Successful';
    if (s === 'pending')                  return 'Pending';
    return 'Failed';
  }
}